import { useMemo } from 'react';
import { useTeachingBlockVersionData } from '../components/TeachingBlockVersion/hooks/useTeachingBlockVersionData';
import { type LessonItemFragment, useLessonsForTeachingBlockVersionQuery } from '../types/planung-graphql-client-defs';
import { averageColor } from '../utils/average-color';
import { generateGradientString } from '../utils/generate-gradient-string';
import { getCardTextColor } from '../utils/getCardTextColor';
import { useLoadBasicData } from './useLoadBasicData';
import { useMemorizedCacheTag } from './useMemorizedCacheTag';

// Die Karten der Epochen-Stecktafel entstehen hier im Frontend aus dem Lesemodell; im Schema gibt es diese
// Typen nicht mehr, seit die ungenutzten Root-Felder (teachingBlockClassGridCards, ...) entfallen sind.
type TeachingBlockGridCardBase = {
  uuid: string;
  teachingBlockCardUuid: string;
  lessonUuid: string;
  colors: string[];
  cssGradientString?: string | null;
  cssHeight?: string | null;
  cssTop?: string | null;
  divisionsCount?: number | null;
  duration?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  includeHoliday: boolean;
  locked?: boolean | null;
  textColor?: string | null;
  usedPosition: number;
};
export type TeachingBlockClassGridCard = TeachingBlockGridCardBase & {
  __typename?: 'TeachingBlockClassGridCard';
  classUuid: string;
};
export type TeachingBlockTeacherGridCard = TeachingBlockGridCardBase & {
  __typename?: 'TeachingBlockTeacherGridCard';
  teacherUuid: string;
};
export type TeachingBlockRoomGridCard = TeachingBlockGridCardBase & {
  __typename?: 'TeachingBlockRoomGridCard';
  roomUuid: string;
};

export type GroupedTeachingBlockClassGridCard = {
  lessons: {
    lessonUuid: string;
    cards: {
      byDuration: {
        biggestDivisionsCount: number;
        duration: number;
        teachingBlockCardsCount: number;
        cards: TeachingBlockClassGridCard[];
      }[];
    };
  }[];
};

type GridDataCards = {
  classGridCards: TeachingBlockClassGridCard[];
  teacherGridCards: TeachingBlockTeacherGridCard[];
  roomGridCards: TeachingBlockRoomGridCard[];
};

type GridCardSource = {
  uuid: string;
  lessonUuid: string;
  duration?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  includeHoliday: boolean;
  locked: boolean;
  roomUuids: string[];
};

type GridCardLesson = Pick<LessonItemFragment, 'uuid' | 'teachers' | 'lessonClasses'>;

type GridCardBasicData = {
  /** Lehrkräfte mit ihrer Stundenplan-Farbe, in der Reihenfolge der Stammdaten. */
  teachers: Array<{ uuid: string; timetableConfig?: { color?: string | null } | null }>;
  divisions: Array<{ uuid: string; groups: Array<{ uuid: string; order: number }> }>;
  rooms: Array<{ uuid: string }>;
};

const EMPTY_GRID_CARDS: GridDataCards = { classGridCards: [], teacherGridCards: [], roomGridCards: [] };

const byLessonAndDuration = <T extends { lessonUuid: string; duration?: number | null }>(a: T, b: T) =>
  a.lessonUuid === b.lessonUuid ? (a.duration ?? 1) - (b.duration ?? 1) : -1;

/**
 * Baut aus den Karten der Version die Grid-Karten je Klasse, Lehrkraft und Raum. Reine Funktion
 * über uuid-Verweise: Karten zeigen auf ihre Zuordnung, die Zuordnung liefert Klassen, Gruppen
 * und Lehrkräfte.
 */
export const buildTeachingBlockGridCards = ({
  cards,
  lessons,
  basicData,
}: {
  cards: GridCardSource[];
  lessons: GridCardLesson[];
  basicData: GridCardBasicData;
}): GridDataCards => {
  const lessonByUuid = new Map(lessons.map((lesson) => [lesson.uuid, lesson]));
  const teacherColorByUuid = new Map(
    basicData.teachers.map((teacher) => [teacher.uuid, teacher.timetableConfig?.color ?? null]),
  );
  const divisionByUuid = new Map(basicData.divisions.map((division) => [division.uuid, division]));

  // Farbverlauf der Zuordnung: die Farben ihrer Lehrkräfte an der Position ihrer Reihenfolge.
  const stylesByLesson = new Map<string, { colors: string[]; cssGradientString?: string; textColor: string }>();
  const stylesOf = (lessonUuid: string) => {
    const cached = stylesByLesson.get(lessonUuid);
    if (cached) return cached;

    const _colors: string[] = [];
    lessonByUuid.get(lessonUuid)?.teachers.forEach((lessonTeacher) => {
      const color = teacherColorByUuid.get(lessonTeacher.uuid);
      if (color) {
        if (_colors[lessonTeacher.order] === undefined) {
          _colors[lessonTeacher.order] = color;
        } else {
          _colors.push(color);
        }
      }
    });
    const colors = _colors.filter((color) => color);
    const styles = {
      colors,
      cssGradientString: generateGradientString(colors) || undefined,
      textColor: getCardTextColor(averageColor(colors)),
    };
    stylesByLesson.set(lessonUuid, styles);
    return styles;
  };

  const classCards: TeachingBlockClassGridCard[] = [];
  const teacherCards: TeachingBlockTeacherGridCard[] = [];
  const roomCards: TeachingBlockRoomGridCard[] = [];

  cards.forEach((card) => {
    const lesson = lessonByUuid.get(card.lessonUuid);
    const { colors, cssGradientString, textColor } = stylesOf(card.lessonUuid);
    const shared = {
      teachingBlockCardUuid: card.uuid,
      lessonUuid: card.lessonUuid,
      locked: card.locked,
      colors,
      startDate: card.startDate,
      endDate: card.endDate,
      duration: card.duration,
      cssGradientString,
      textColor,
      includeHoliday: card.includeHoliday ?? false,
    };

    // classes
    lesson?.lessonClasses.forEach((lessonClass) => {
      const division = lessonClass.usedDivision ? divisionByUuid.get(lessonClass.usedDivision.uuid) : undefined;
      const divisionsCount = division?.groups.length ?? 1;

      if (lessonClass.groups.length > 0) {
        lessonClass.groups.forEach((group) => {
          division?.groups.forEach(({ order, uuid }) => {
            if (uuid === group.uuid) {
              const height = 100 / divisionsCount;
              classCards.push({
                __typename: 'TeachingBlockClassGridCard',
                ...shared,
                uuid: `${group.uuid}_${card.uuid}`,
                classUuid: lessonClass.class.uuid,
                divisionsCount,
                usedPosition: order,
                cssHeight: `${height.toFixed(3)}%`,
                cssTop: `${(height * order).toFixed(3)}%`,
              });
            }
          });
        });
      } else {
        classCards.push({
          __typename: 'TeachingBlockClassGridCard',
          ...shared,
          uuid: `${lessonClass.class.uuid}_${card.uuid}`,
          classUuid: lessonClass.class.uuid,
          divisionsCount: 1,
          usedPosition: 1,
          cssHeight: `100%`,
          cssTop: '0',
        });
      }
    });
  });

  // teachers: alle Lehrkräfte der Zuordnung, in der Reihenfolge der Stammdaten
  cards.forEach((card) => {
    const lessonTeacherUuids = new Set(lessonByUuid.get(card.lessonUuid)?.teachers.map((teacher) => teacher.uuid));
    const { colors, cssGradientString, textColor } = stylesOf(card.lessonUuid);

    basicData.teachers.forEach((teacher) => {
      if (!lessonTeacherUuids.has(teacher.uuid)) return;
      teacherCards.push({
        __typename: 'TeachingBlockTeacherGridCard',
        uuid: `${teacher.uuid}_${card.uuid}`,
        teacherUuid: teacher.uuid,
        teachingBlockCardUuid: card.uuid,
        lessonUuid: card.lessonUuid,
        locked: card.locked,
        colors,
        startDate: card.startDate,
        endDate: card.endDate,
        duration: card.duration,
        usedPosition: 0,
        cssGradientString,
        textColor,
        includeHoliday: card.includeHoliday ?? false,
      });
    });
  });

  // rooms: die der Karte zugewiesenen Räume, in der Reihenfolge der Stammdaten
  cards.forEach((card) => {
    const cardRoomUuids = new Set(card.roomUuids);
    const { colors, cssGradientString, textColor } = stylesOf(card.lessonUuid);

    basicData.rooms.forEach((room) => {
      if (!cardRoomUuids.has(room.uuid)) return;
      roomCards.push({
        __typename: 'TeachingBlockRoomGridCard',
        uuid: `${room.uuid}_${card.uuid}`,
        roomUuid: room.uuid,
        teachingBlockCardUuid: card.uuid,
        lessonUuid: card.lessonUuid,
        locked: card.locked,
        colors,
        startDate: card.startDate,
        endDate: card.endDate,
        duration: card.duration,
        usedPosition: 0,
        cssGradientString,
        textColor,
        includeHoliday: card.includeHoliday ?? false,
      });
    });
  });

  return {
    classGridCards: classCards.sort(byLessonAndDuration),
    teacherGridCards: teacherCards.sort(byLessonAndDuration),
    roomGridCards: roomCards.sort(byLessonAndDuration),
  };
};

export const useTeachingBlockDataCards = (versionUuid: string): GridDataCards => {
  const lessonContext = useMemorizedCacheTag('LESSONS');
  const { versionData } = useTeachingBlockVersionData(versionUuid);

  const [{ data: lessonsData }] = useLessonsForTeachingBlockVersionQuery({
    variables: { versionUuid },
    context: lessonContext,
    pause: !versionUuid || versionUuid === '',
  });

  const { teacherData, divisionsData, roomsData } = useLoadBasicData({
    pause: !versionUuid || versionUuid === '',
  });

  const cards = versionData?.cards;
  const lessons = lessonsData?.lessonsForTeachingBlockVersion;

  return useMemo(() => {
    if (!cards || !lessons) return EMPTY_GRID_CARDS;
    return buildTeachingBlockGridCards({
      cards,
      lessons,
      basicData: {
        teachers: teacherData?.people ?? [],
        divisions: divisionsData?.divisions ?? [],
        rooms: roomsData?.rooms ?? [],
      },
    });
  }, [cards, lessons, teacherData?.people, divisionsData?.divisions, roomsData?.rooms]);
};
